import { _assert } from '@naturalcycles/js-lib';
/**
 * A simple wrapper around Web Crypto API to sign and verify data.
 *
 * It should not be instantiated directly. Instead, use `signingKeyVault` factory function,
 * which works in the same fashion as the `localDate` factory function.
 */
export class SigningKeyVault {
  constructor() {
    this.defaultAlgorithm = {
      name: 'RSA-PSS',
      hash: 'SHA-256',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      params: {
        name: 'RSA-PSS',
        saltLength: 32
      }
    };
  }
  /**
   * Generates a new RSA key pair to sign or verify.
   */
  async generateKeyPair() {
    _assert(!this.publicKey && !this.privateKey, 'Some keys are already available. One instance may handle one set of keys only.');
    const {
      privateKey,
      publicKey
    } = await crypto.subtle.generateKey(this.defaultAlgorithm, true, ['sign', 'verify']);
    this.privateKey = privateKey;
    this.publicKey = publicKey;
    this.currentAlgorithm = this.defaultAlgorithm;
  }
  /**
   * Exports the key pair, both private (!) and public keys.
   * The result can be stored and later used with `importKeyPair`.
   */
  async exportKeyPair() {
    _assert(this.privateKey && this.publicKey, 'Key pair is not generated yet. Call `generateKeyPair` first.');
    _assert(this.currentAlgorithm, 'Algorithm is not set. This is a bug.');
    const privateKey = await crypto.subtle.exportKey('jwk', this.privateKey);
    const publicKey = await crypto.subtle.exportKey('jwk', this.publicKey);
    return {
      version: 1,
      privateKey,
      publicKey,
      name: this.currentAlgorithm.name,
      hash: this.currentAlgorithm.hash,
      params: this.currentAlgorithm.params
    };
  }
  /**
   * Imports a key pair that was previously exported with `exportKeyPair`.
   */
  async importKeyPair(keyTransport) {
    _assert(!this.publicKey && !this.privateKey, 'Some keys are already available. One instance may handle one set of keys only.');
    this.privateKey = await crypto.subtle.importKey('jwk', keyTransport.privateKey, keyTransport, true, ['sign']);
    this.publicKey = await crypto.subtle.importKey('jwk', keyTransport.publicKey, keyTransport, true, ['verify']);
    this.currentAlgorithm = {
      name: keyTransport.name,
      hash: keyTransport.hash,
      params: keyTransport.params
    };
  }
  /**
   * Exports the public key only.
   * The result can be stored and later used with `importPublicKey`.
   */
  async exportPublicKey() {
    _assert(this.publicKey, 'Public key is missing.');
    const publicKey = await crypto.subtle.exportKey('jwk', this.publicKey);
    return {
      version: 1,
      publicKey,
      name: this.defaultAlgorithm.name,
      hash: this.defaultAlgorithm.hash,
      params: this.defaultAlgorithm.params
    };
  }
  /**
   * Imports a public key that was previously exported with `exportPublicKey`.
   * This is useful when you only need to verify signatures.
   */
  async importPublicKey(publicKeyTransport) {
    _assert(!this.publicKey && !this.privateKey, 'Some keys are already available. One instance may handle one set of keys only.');
    this.publicKey = await crypto.subtle.importKey('jwk', publicKeyTransport.publicKey, publicKeyTransport, true, ['verify']);
    this.currentAlgorithm = {
      name: publicKeyTransport.name,
      hash: publicKeyTransport.hash,
      params: publicKeyTransport.params
    };
  }
  /**
   * Signs the data with the private key.
   */
  async sign(data) {
    _assert(this.privateKey, 'Private key is needed for signing.');
    _assert(this.currentAlgorithm, 'Algorithm is not set. This is a bug.');
    const bufferData = new TextEncoder().encode(data);
    const signature = await crypto.subtle.sign(this.currentAlgorithm.params, this.privateKey, bufferData);
    return this.toBase64(signature);
  }
  /**
   * Verifies the data with the public key.
   */
  async verify(signature, data) {
    _assert(this.publicKey, 'Public key is needed for verification.');
    _assert(this.currentAlgorithm, 'Algorithm is not set. This is a bug.');
    const bufferSignature = this.fromBase64(signature);
    const bufferData = new TextEncoder().encode(data);
    return await crypto.subtle.verify(this.currentAlgorithm.params, this.publicKey, bufferSignature, bufferData);
  }
  toBase64(buffer) {
    return btoa(String.fromCodePoint(...new Uint8Array(buffer)));
  }
  fromBase64(base64) {
    return Uint8Array.from(atob(base64), c => c.codePointAt(0) || 0);
  }
}
export class SigningKeyVaultFactory {
  /**
   * Creates a new instance of `SigningKeyVault` and initializes it with the provided key.
   */
  async from(key) {
    const asymmetricCrypto = new SigningKeyVault();
    if ('privateKey' in key) {
      await asymmetricCrypto.importKeyPair(key);
    } else {
      await asymmetricCrypto.importPublicKey(key);
    }
    return asymmetricCrypto;
  }
  /**
   * Creates a new instance of `SigningKeyVault` and initializes it with the provided key.
   * If the key is `null` or `undefined`, returns `null`.
   */
  async fromOrNull(key) {
    if (!key) return null;
    return await this.from(key);
  }
  /**
   * Creates a new instance of `SigningKeyVault` and generates a new RSA key pair to sign or verify.
   */
  async generateKeyPair() {
    const asymmetricCrypto = new SigningKeyVault();
    await asymmetricCrypto.generateKeyPair();
    return asymmetricCrypto;
  }
}
const signingKeyVaultFactory = new SigningKeyVaultFactory();
/**
 * When used as a function, it takes a public or a private&public key and initializes a SigningKeyVault with the provided key.
 * The SigningKeyVault then can be used to sign or verify data - depending on the available keys.
 *
 * It also provides helper methods to generate a new key pair or import keys.
 */
export const signingKeyVault = signingKeyVaultFactory.from.bind(signingKeyVaultFactory);
Object.setPrototypeOf(signingKeyVault, signingKeyVaultFactory);